import type { SubEntryType } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActionButton,
  ActionSubmit,
  Dialog,
  DialogButton,
  DialogButtons,
  ModalButtons,
  DialogContent,
} from "@tandemdiabetes/sugarmate-react-components"
import type {
  FunctionComponent,
  MouseEventHandler,
} from "react"
import {
  useCallback,
  useState,
} from "react"

import { useEntryModalPrivateContext } from "./EntryModalPrivateContext"
import { useEntryModalPublicContext } from "./EntryModalPublicContext"
import { usePolyglot } from "src/contexts"
import styles from "src/styles/entryModal.module.scss"
import type {
  EditableSubEntry,
  EditableUserEntry,
} from "src/types"

const polyglotPrefix = "components.entry_modal."

interface Props {
  allowCreateWithoutChanges?: boolean;
  currentUserEntry: EditableUserEntry;
  currentSubEntry: EditableSubEntry<SubEntryType>;
  // a dialog heading and / or body that will be shown as a warning to the user
  SaveWarning?: FunctionComponent;
}

export function SubEntryFormButtons(props: Props) {
  const {
    currentUserEntry,
    currentSubEntry,
    SaveWarning,
    allowCreateWithoutChanges,
  } = props

  const polyglot = usePolyglot()

  const {
    addSubEntry,
    showEntry,
    formId,
    isUnsavedEntryTemplateInstance,
  } = useEntryModalPrivateContext()

  const { hasUnsavedChanges } = useEntryModalPublicContext()

  const [
    warningDialogOpen,
    setWarningDialogOpen,
  ] = useState<boolean>(false)

  const openWarningDialog: MouseEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      e.preventDefault()
      setWarningDialogOpen(true)
    },
    [setWarningDialogOpen],
  )

  const closeSubEntry: MouseEventHandler<HTMLButtonElement | HTMLInputElement> = useCallback(
    (e) => {
      e.preventDefault()
      showEntry()
    },
    [showEntry],
  )

  const closeDialog: MouseEventHandler<HTMLInputElement> = useCallback(
    () => {
      setWarningDialogOpen(false)
    },
    [setWarningDialogOpen],
  )

  const addItem: MouseEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      e.preventDefault()
      addSubEntry()
    },
    [addSubEntry],
  )

  let numberOfSubEntries = currentUserEntry.subentries.length
  if (!currentSubEntry._id) {
    numberOfSubEntries += 1
  }

  return (
    <Dialog
      open={warningDialogOpen}
      onOpenChange={setWarningDialogOpen}
    >
      <ModalButtons className={styles.reverseButtonOrder}>
        {numberOfSubEntries > 1 ? (
          // this is ActionSubmit instead of button so that it
          // is disabled / enabled based on form validation
          <ActionSubmit
            className={styles.dialogMultiButton}
            onClick={SaveWarning ? openWarningDialog : closeSubEntry}
            value={polyglot.t(`${polyglotPrefix}done_cta`)}
            id={"sub-entry-done-button"}
          />
        ) : (
          <ActionSubmit
            onClick={SaveWarning ? openWarningDialog : undefined}
            className={styles.dialogMultiButton}
            disabled={
              !hasUnsavedChanges &&
              !(allowCreateWithoutChanges && !currentUserEntry.id) &&
              !isUnsavedEntryTemplateInstance
            }
            value={polyglot.t(`${polyglotPrefix}save_cta`)}
            id={"sub-entry-save-button"}
          />
        )}
        <ActionSubmit
          stylePreset="secondary"
          className={styles.dialogMultiButton}
          onClick={addItem}
          value={polyglot.t(`${polyglotPrefix}add_item_cta`)}
          id="add-another-item-button"
        />
      </ModalButtons>
      <DialogContent>
        {SaveWarning && <SaveWarning />}
        <DialogButtons>
          <DialogButton stylePreset="secondary">
            {polyglot.t(`${polyglotPrefix}cancel_cta`)}
          </DialogButton>
          {numberOfSubEntries > 1 ? (
            <ActionButton
              className={styles.dialogMultiButton}
              onClick={closeSubEntry}
            >
              {polyglot.t(`${polyglotPrefix}done_cta`)}
            </ActionButton>
          ) : (
            <ActionSubmit
              className={styles.dialogMultiButton}
              value={polyglot.t(`${polyglotPrefix}save_cta`)}
              onClick={closeDialog}
              form={formId}
            />
          )}
        </DialogButtons>
      </DialogContent>
    </Dialog>
  )
}
